// Monthly-EMI repayment allocation for Business Loan and GP Loan: money pays the
// oldest instalment first, interest before principal. Pure functions, no imports.
// The backend keeps an identical copy in backend/utils/loanAllocation.js - change both.

export const OVERDUE_TOLERANCE = 10;

const VOIDED_STATUSES = ["rejected", "cancelled", "retriggered", "deleted"];
const IST_OFFSET_MS = 330 * 60 * 1000;
const DAY_MS = 86400000;

const parseYmd = (ymd) => {
  const [y, m, d] = ymd.split("-").map(Number);
  return { y, m, d };
};

const ymdFromUtcMs = (ms) => new Date(ms).toISOString().slice(0, 10);

const daysInMonth = (y, m) => new Date(Date.UTC(y, m, 0)).getUTCDate();

const addMonthsKeepingDay = (ymd, months, dueDay) => {
  const { y, m, d } = parseYmd(ymd);
  const target = new Date(Date.UTC(y, m - 1 + months, 1));
  const ty = target.getUTCFullYear();
  const tm = target.getUTCMonth() + 1;
  const day = Math.min(dueDay || d, daysInMonth(ty, tm));
  return `${ty}-${String(tm).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
};

export const daysBetween = (fromYmd, toYmd) => {
  const a = parseYmd(fromYmd);
  const b = parseYmd(toYmd);
  return Math.round((Date.UTC(b.y, b.m - 1, b.d) - Date.UTC(a.y, a.m - 1, a.d)) / DAY_MS);
};

const toAmount = (value) => {
  const n = parseFloat(String(value ?? "").replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
};

// Database timestamps are stored in UTC (Sequelize default), so a timestamp is
// shifted to Indian time before taking its calendar date. A plain date is used as-is.
export const toIstDateOnly = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : ymdFromUtcMs(value.getTime() + IST_OFFSET_MS);
  }
  const text = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;
  let iso = text;
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}(:\d{2})?$/.test(text)) iso = `${text.replace(" ", "T")}Z`;
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? null : ymdFromUtcMs(ms + IST_OFFSET_MS);
};

const isApprovalVoided = (approval) =>
  !!approval &&
  (approval.fieldManagerStatus === "retrigger" ||
    approval.branchManagerStatus === "retrigger" ||
    approval.misStatus === "retrigger" ||
    approval.accountManagerStatus === "retrigger" ||
    approval.branchManagerStatus === "rejected" ||
    approval.misStatus === "rejected" ||
    approval.accountManagerStatus === "rejected");

// A "pending" receipt is a partial or not-yet-approved collection; it counts once
// the Accounts Manager has approved it.
export const isCountableReceipt = (receipt, approval) => {
  if (!receipt) return false;
  if (toAmount(receipt.receivedAmount) <= 0) return false;
  const status = String(receipt.status || "").trim().toLowerCase();
  if (VOIDED_STATUSES.includes(status)) return false;
  if (isApprovalVoided(approval)) return false;
  if (status === "paid") return true;
  if (status === "pending") return !!approval && approval.accountManagerStatus === "submitted";
  return false;
};

export const receiptMoneyDate = (receipt) =>
  toIstDateOnly(receipt.collectedDate) || toIstDateOnly(receipt.emiDate);

// dueDay keeps a 29th-31st EMI day after a short first month; defaults to the first due date's day.
export const buildRepaymentSchedule = ({ loanAmount, annualRatePercent, tenure, emiAmount, firstDueDate, dueDay }) => {
  const principal = Math.round(toAmount(loanAmount));
  const rate = toAmount(annualRatePercent);
  const count = parseInt(tenure, 10);
  const emi = Math.round(toAmount(emiAmount));
  const first = toIstDateOnly(firstDueDate);
  const day = parseInt(dueDay, 10);
  const keepDay = day >= 1 && day <= 31 ? day : undefined;
  if (principal <= 0 || rate < 0 || !(count > 0) || emi <= 0 || !first) return null;

  const schedule = [];
  let balance = principal;
  for (let k = 0; k < count && balance > 0; k++) {
    const interest = Math.round((balance * rate) / 12 / 100);
    const isLast = k === count - 1;
    const principalPart = isLast ? balance : Math.max(0, Math.min(balance, emi - interest));
    schedule.push({
      number: k + 1,
      dueDate: addMonthsKeepingDay(first, k, keepDay),
      interest,
      principal: principalPart,
      amount: interest + principalPart,
    });
    balance -= principalPart;
  }
  return schedule;
};

// receipts: [{ id, amount, moneyDate }] already filtered to countable ones.
export const allocatePayments = ({ schedule, receipts, asAtDate, tolerance = OVERDUE_TOLERANCE }) => {
  const asAt = toIstDateOnly(asAtDate);
  const money = (receipts || [])
    .filter((r) => r && r.amount > 0 && r.moneyDate && (!asAt || r.moneyDate <= asAt))
    .sort((a, b) => (a.moneyDate === b.moneyDate ? String(a.id).localeCompare(String(b.id), undefined, { numeric: true }) : a.moneyDate < b.moneyDate ? -1 : 1))
    .map((r) => ({ id: r.id, amount: r.amount, left: r.amount, moneyDate: r.moneyDate }));

  let cursor = 0;
  // allocations records how much of each receipt went to this instalment's interest and principal.
  const take = (needed, receiptIds, allocations, part) => {
    let paid = 0;
    while (paid < needed && cursor < money.length) {
      const r = money[cursor];
      const used = Math.min(r.left, needed - paid);
      r.left -= used;
      paid += used;
      if (used > 0) {
        if (!receiptIds.includes(r.id)) receiptIds.push(r.id);
        let entry = allocations.find((a) => a.receiptId === r.id);
        if (!entry) {
          entry = { receiptId: r.id, interest: 0, principal: 0 };
          allocations.push(entry);
        }
        entry[part] += used;
      }
      if (r.left <= 0) cursor++;
    }
    return paid;
  };

  const instalments = schedule.map((inst) => {
    const receiptIds = [];
    const allocations = [];
    const paidInterest = take(inst.interest, receiptIds, allocations, "interest");
    const paidPrincipal = take(inst.principal, receiptIds, allocations, "principal");
    const paid = paidInterest + paidPrincipal;
    const status = paid >= inst.amount - 1 ? "Paid" : paid > 0 ? "Partial" : "Unpaid";
    return { ...inst, paidInterest, paidPrincipal, paid, status, isDue: !!asAt && inst.dueDate <= asAt, receiptIds, allocations };
  });

  const sum = (f) => instalments.reduce((s, i) => s + f(i), 0);
  const principalPaid = sum((i) => i.paidPrincipal);
  const interestPaid = sum((i) => i.paidInterest);
  const loanPrincipal = sum((i) => i.principal);
  const overdueInterest = sum((i) => (i.isDue ? i.interest - i.paidInterest : 0));
  const overduePrincipal = sum((i) => (i.isDue ? i.principal - i.paidPrincipal : 0));
  const overdueTotal = overdueInterest + overduePrincipal;
  const oldest = overdueTotal > tolerance ? instalments.find((i) => i.isDue && i.amount - i.paid > 1) : null;

  return {
    instalments,
    principalPaid,
    interestPaid,
    totalPaid: principalPaid + interestPaid,
    outstandingPrincipal: loanPrincipal - principalPaid,
    outstandingInterest: sum((i) => i.interest) - interestPaid,
    overduePrincipal: overdueTotal > tolerance ? overduePrincipal : 0,
    overdueInterest: overdueTotal > tolerance ? overdueInterest : 0,
    shortfall: overdueTotal,
    oldestUnpaidDueDate: oldest ? oldest.dueDate : null,
    dpd: oldest ? Math.max(0, daysBetween(oldest.dueDate, asAt)) : 0,
    instalmentsFullyCleared: instalments.filter((i) => i.status === "Paid").length,
    excess: money.reduce((s, r) => s + r.left, 0),
    unusedReceiptIds: money.filter((r) => r.left === r.amount).map((r) => r.id),
    // Money not yet needed by any instalment, per receipt (whole or part of a receipt).
    leftovers: money.filter((r) => r.left > 0).map((r) => ({ receiptId: r.id, amount: r.left })),
  };
};

// Convenience wrapper for a loan row as the API returns it.
export const summarizeLoanAllocation = ({
  loanAmount,
  annualRatePercent,
  tenure,
  emiAmount,
  firstDueDate,
  dueDay,
  receipts,
  getApproval = () => null,
  asAtDate,
  tolerance,
}) => {
  const schedule = buildRepaymentSchedule({ loanAmount, annualRatePercent, tenure, emiAmount, firstDueDate, dueDay });
  if (!schedule) return null;
  const countable = (receipts || [])
    .filter((r) => isCountableReceipt(r, getApproval(r)))
    .map((r) => ({ id: r.id, amount: toAmount(r.receivedAmount), moneyDate: receiptMoneyDate(r) }));
  return allocatePayments({ schedule, receipts: countable, asAtDate, tolerance });
};
